import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { darken } from '@material-ui/core/styles/colorManipulator';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import PlusIcon from '@material-ui/icons/Add';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import { default as CloseIcon } from '@material-ui/icons/Close';
import Autocomplete from '@material-ui/lab/Autocomplete';
import classNames from 'classnames';
import { customAlphabet } from 'nanoid/non-secure';
import React, { useEffect, useState } from 'react';
import WindsorApi from '../../../api/WindsorApi';
import FaroParameterListInput from '../../../faro/parameters/FaroParameterListInput';
import ActionParamSetListInput from './ActionParamSetListInput';

const nanoid = customAlphabet('_0123456789abcdefghijklmnopqrstuvwxyz', 5);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      marginBottom: 16,
      padding: '4px 8px 4px 8px',
      backgroundColor: darken(theme.palette.background.default, 0.03)
    },
    section: {
      marginBottom: 32
    },
    dialogTitle: {
      display: 'flex',
      justifyContent: 'space-between'
    },
    closeButton: {
      padding: 4
    },
    sectionContent: {
      padding: 8
    },
    textField: {
      marginBottom: 16
    },
    autocompleteCheckbox: {
      marginRight: 8
    },
    parameterSetSectionTitle: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    addButton: {
      textTransform: 'none'
    },
    tableHead: {
      backgroundColor: 'inherit'
    }
  })
);

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const GENERATED_ACTION_NAME_REGEX = new RegExp(`^-\\w{5}$`);
const SECURITY_GROUP_REGEX = new RegExp('sg-[a-z0-9]{8,17}');
const SUBNET_REGEX = new RegExp('subnet-[a-z0-9]{8,17}');
const ROLE_REGEX = new RegExp('arn:aws(-us-gov|-eusc)?:iam::\\d*:role/.*');
const OUTPUT_PARAM_REGEX = new RegExp('((DeploymentRecipe..*.Outputs..*))');

interface Props {
  familyId: string;
  accountId: string;
  region: string;
  tests: any[];
  action: any;
  testActions: any[];
  deploymentActions: any[];
  onClose: () => void;
  onChange: (action: any) => void;
}

const windsorApi = new WindsorApi();

function strToList(val) {
  return val.split(/[ ,]+/).map(item => item.trim()).filter(item => item !== '');
}

function getOuputParamErrors(val, deploymentActions, dependencies) {
  const errors = [];
  if (val.indexOf('.') !== -1) {
    if (OUTPUT_PARAM_REGEX.test(val)) {
      const actionName = val.split('.')[1];
      if (!deploymentActions.some(action => action.ActionName === actionName)) {
        errors.push(`Attempting to use an output from a Deployment Action "${actionName}" that is not in this Workflow`);
      }

      if (!dependencies.includes(actionName)) {
        errors.push(`Attempting to use an output from a Deployment Action "${actionName}" that is not selected as a direct dependency`);
      }
    } else {
      errors.push(`Invalid value: ${val}, deployment outputs must be in the form of DeploymentRecipe.{ActionName}.Outputs.{Value}`);
    }
  }
  return errors;
}

function isValueParameterized(val) {
  return val.indexOf('((') !== -1 && val.indexOf('))') !== -1;
}

function getValidationErrors({
  deploymentActions,
  dependencies,
  securityGroups,
  subnets,
  testName,
  testVersion,
  actionName,
  actionNameSet,
  assumeRoleArn
}) {
  const errors = {
    actionName: [],
    testName: [],
    testVersion: [],
    securityGroups: [],
    subnets: [],
    assumeRoleArn: []
  };

  if (!actionName) {
    errors.actionName.push('Action name is required');
  }

  if (actionNameSet.has(actionName)) {
    errors.actionName.push('Action name already exists');
  }

  if (!testName) {
    errors.testName.push('Test name is required');
  }

  if (!testVersion) {
    errors.testVersion.push('Test version is required');
  }

  if (assumeRoleArn && !ROLE_REGEX.test(assumeRoleArn)) {
    if (!isValueParameterized(assumeRoleArn)) {
      errors.assumeRoleArn.push('Invalid role arn');
    } else {
      errors.assumeRoleArn.push(...getOuputParamErrors(assumeRoleArn, deploymentActions, dependencies));
    }
  }

  const sgList = strToList(securityGroups);
  const subnetList = strToList(subnets);

  // Check SGs
  if (sgList.length > 4) {
    errors.securityGroups.push('Cannot have more than 4 security groups');
  }

  if (sgList.length > 0 && subnetList.length === 0) {
    errors.securityGroups.push('Cannot have security group(s) without subnets');
  }

  errors.securityGroups.push(...sgList.flatMap(sg => {
    const messages = [];
    if (!SECURITY_GROUP_REGEX.test(sg)) {
      if (!isValueParameterized(sg)) {
        messages.push(`Security group ${sg} must be in the form of sg-[a-z0-9]{8}`);
      } else {
        messages.push(...getOuputParamErrors(sg, deploymentActions, dependencies));
      }
    }
    return messages;
  }));

  // Check subnets
  if (subnetList.length > 16) {
    errors.securityGroups.push('Cannot have more than 16 subnets');
  }

  errors.subnets.push(...subnetList.flatMap(subnet => {
    const messages = [];
    if (!SUBNET_REGEX.test(subnet)) {
      if (!isValueParameterized(subnet)) {
        messages.push(`Subnet ${subnet} must be in the form of subnet-[a-z0-9]{8,17}`);
      } else {
        messages.push(...getOuputParamErrors(subnet, deploymentActions, dependencies));
      }
    }
    return messages;
  }));

  return errors;
}

function TestActionDialog(props: Props) {
  const { familyId, accountId, region, tests, action, testActions, deploymentActions, onClose, onChange } = props;
  const classes = useStyles({});

  const [actionName, setActionName] = useState(action.ActionName || '');
  const [autogenerateActionName, setAutogenerateActionName] = useState(!action.TestName || (action.ActionName.startsWith(`${action.TestName}`) && GENERATED_ACTION_NAME_REGEX.test(action.ActionName.substring(action.TestName.length))));
  const actionNameSlug = useState(autogenerateActionName && action.DeckName ? action.ActionName.substring(action.ActionName.length - 5) : nanoid())[0];
  const [testName, setTestName] = useState(action.TestName || null);
  const [testVersion, setTestVersion] = useState(action.Version || null);
  const [testVersions, setTestVersions] = useState([]);
  const [securityGroups, setSecurityGroups] = useState(action.SecurityGroups ? action.SecurityGroups.join(', ') : []);
  const [subnets, setSubnets] = useState(action.Subnets ? action.Subnets.join(', ') : []);
  const [assumeRoleArn, setAssumeRoleArn] = useState(action.AssumeRoleARN || '');
  const [dependencies, setDependencies] = useState(action.Dependencies || [])
  const [parameters, setParameters] = useState(Object.keys(action.Parameters).length > 0 ? Object.keys(action.Parameters).map(key => ({ Name: key, Value: action.Parameters[key] })) : []);
  const [paramSets, setParamSets] = useState(action.ParameterSets ? action.ParameterSets.map(paramSet => ({ name: paramSet.Name, version: paramSet.Version })) : []);
  const [showParameters, setShowParameters] = useState(parameters.length > 0);
  const [showErrors, setShowErrors] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    actionName: [],
    testName: [],
    testVersion: [],
    securityGroups: [],
    subnets: [],
    assumeRoleArn: []
  });
  const [actionNameSet, setActionNameSet] = useState(new Set());

  useEffect(() => {
    setActionNameSet([...testActions, ...deploymentActions].reduce((accumulator, item) => {
      accumulator.add(item.ActionName);
      return accumulator;
    }, new Set()));
  }, [testActions, deploymentActions]);

  useEffect(() => {
    if (testName) {
      windsorApi.getTestVersions(familyId, testName).then(versions => setTestVersions(['latest', ...versions]));
    }
  }, [testName, familyId]);

  useEffect(() => {
    if (showErrors) {
      setValidationErrors(getValidationErrors({
        deploymentActions,
        dependencies,
        securityGroups,
        subnets,
        testName,
        testVersion,
        actionName,
        actionNameSet,
        assumeRoleArn
      }));
    }
  }, [showErrors, securityGroups, subnets, deploymentActions, dependencies, testName, testVersion, actionName, actionNameSet, assumeRoleArn]);

  const onUpdate = () => {
    const errors = getValidationErrors({
      deploymentActions,
      dependencies,
      securityGroups,
      subnets,
      testName,
      testVersion,
      actionName,
      actionNameSet,
      assumeRoleArn
    });
    // Check for required fields
    if (Object.keys(errors).some(key => !!errors[key].length)) {
      setShowErrors(true);
    } else {
      onChange({
        ActionName: actionName,
        TestName: testName,
        Version: testVersion,
        AssumeRoleARN: assumeRoleArn,
        SecurityGroups: strToList(securityGroups),
        Subnets: strToList(subnets),
        Dependencies: dependencies,
        ParameterSets: paramSets.map(paramSet => ({ Name: paramSet.name, Version: paramSet.version })),
        Parameters: parameters.reduce((accumulator, param) => {
          accumulator[param.Name] = param.Value;
          return accumulator;
        }, {})
      });
    }
  };

  const onAddParamSet = () => {
    const paramSetsCopy = paramSets.slice();
    paramSetsCopy.push({ name: '', version: '' });
    setParamSets(paramSetsCopy);
  };

  const onDependenciesChange = (event: React.ChangeEvent<{}>, value: any) => {
    setDependencies(value);
  };

  const onTestNameChange = (event: React.ChangeEvent<{}>, value: any) => {
    const newTestName = value ? value.name : null;
    setTestName(newTestName);
    setTestVersion(null);
    setTestVersions([]);
    if (autogenerateActionName) {
      setActionName(newTestName ? `${newTestName}-${actionNameSlug}` : '');
    }
  };

  const onTestVersionChange = (event: React.ChangeEvent<{}>, value: string) => {
    setTestVersion(value);
  };

  const onAutogenerateActionNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAutogenerateActionName(event.target.checked);
    if (event.target.checked) {
      setActionName(testName ? `${testName}-${actionNameSlug}` : '');
    }
  };

  return (
    <Dialog
      open={true}
      scroll="paper"
      onClose={onClose}
      aria-labelledby="test-action-dialog-title"
      aria-describedby="test-action-dialog-description"
      maxWidth="md"
      fullWidth
      disableBackdropClick
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography variant="h6" color="primary">Test Action</Typography>
        <IconButton onClick={onClose} className={classes.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form noValidate autoComplete="off">

          <div className={classes.section}>
            <Typography variant="subtitle1" className={classes.title}>Test Details</Typography>
            <div className={classes.sectionContent}>
              <Autocomplete
                className={classes.textField}
                options={tests}
                value={testName}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
                onChange={onTestNameChange}
                getOptionSelected={(option, value) => value ? option.name === value : false}
                renderInput={(params) => <TextField
                  {...params}
                  label='Test Name'
                  error={showErrors && !!validationErrors.testName.length}
                  helperText={showErrors ? validationErrors.testName.join('. ') : ''}
                  InputLabelProps={{
                    shrink: true
                  }} />}
              />
              <Autocomplete
                options={testVersions}
                value={testVersion}
                getOptionLabel={(option) => option}
                onChange={onTestVersionChange}
                noOptionsText="No test selected"
                renderInput={(params) => <TextField
                  {...params}
                  label='Test Version'
                  error={showErrors && !!validationErrors.testVersion.length}
                  helperText={showErrors ? validationErrors.testVersion.join('. ') : ''}
                  InputLabelProps={{
                    shrink: true
                  }} />}
              />
            </div>
          </div>
          <div className={classes.section}>
            <Typography variant="subtitle1" className={classes.title}>Action Details</Typography>
            <div className={classes.sectionContent}>
              <TextField
                label="Action Name"
                disabled={autogenerateActionName}
                fullWidth
                required
                error={showErrors && !!validationErrors.actionName.length}
                helperText={showErrors ? validationErrors.actionName.join('. ') : ''}
                value={actionName}
                onChange={(event) => setActionName(event.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={autogenerateActionName}
                          onChange={onAutogenerateActionNameChange}
                          name="autogenerateActionName"
                          color="default"
                        />
                      }
                      label="Autogenerate"
                    />
                  </InputAdornment>,
                }}
                InputLabelProps={{
                  shrink: true
                }} />
            </div>
          </div>
          <div className={classes.section}>
            <Typography variant="subtitle1" className={classes.title}>Test Lambda Details</Typography>
            <div className={classes.sectionContent}>
              <TextField
                className={classes.textField}
                fullWidth
                label="Security Groups"
                value={securityGroups}
                onChange={event => setSecurityGroups(event.target.value)}
                error={showErrors && !!validationErrors.securityGroups.length}
                helperText={showErrors ? validationErrors.securityGroups.join('. ') : ''}
                InputLabelProps={{
                  shrink: true
                }}
              />
              <TextField
                fullWidth
                label="Subnets"
                value={subnets}
                onChange={event => setSubnets(event.target.value)}
                error={showErrors && !!validationErrors.subnets.length}
                helperText={showErrors ? validationErrors.subnets.join('. ') : ''}
                InputLabelProps={{
                  shrink: true
                }}
              />
            </div>
          </div>
          <div className={classes.section}>
            <Typography variant="subtitle1" className={classes.title}>IAM Role</Typography>
            <div className={classes.sectionContent}>
              <TextField
                value={assumeRoleArn}
                onChange={(event) => setAssumeRoleArn(event.target.value)}
                fullWidth
                label="Custom IAM Role (optional - some read-only permissions provided by default)"
                error={showErrors && !!validationErrors.assumeRoleArn.length}
                helperText={showErrors ? validationErrors.assumeRoleArn.join('. ') : ''}
                InputLabelProps={{
                  shrink: true
                }} />
            </div>
          </div>
          <div className={classes.section}>
            <Typography variant="subtitle1" className={classes.title}>Dependencies</Typography>
            <div className={classes.sectionContent}>
              <Autocomplete
                value={dependencies}
                onChange={onDependenciesChange}
                options={deploymentActions.map(item => item.ActionName)}
                getOptionLabel={(option) => option}
                multiple
                size="small"
                renderOption={(option, { selected }) => (
                  <React.Fragment>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      className={classes.autocompleteCheckbox}
                      checked={selected}
                    />
                    {option}
                  </React.Fragment>
                )}
                renderInput={(params) => <TextField {...params} label="Deployment Action Dependencies" margin="normal" InputLabelProps={{
                  shrink: true
                }} />}
              />
            </div>
          </div>
          <div className={classes.section}>
            <div className={classNames(classes.title, classes.parameterSetSectionTitle)}>
              <Typography variant="subtitle1">Parameter Sets</Typography>
              {paramSets.length > 0 && <Button className={classes.addButton} onClick={onAddParamSet}><PlusIcon /> Add Parameter Set</Button>}
            </div>
            <div>
              {paramSets.length === 0 && <Button className={classes.addButton} onClick={onAddParamSet}><PlusIcon /> Add Parameter Set</Button>}
              {paramSets.length > 0 && <ActionParamSetListInput
                accountId={accountId}
                region={region}
                paramSets={paramSets}
                onParamSetsChange={newParamSets => setParamSets(newParamSets)}
              />}
            </div>
          </div>
          <div className={classes.section}>
            <Typography variant="subtitle1" className={classes.title}>Parameters</Typography>
            <div>
              {!showParameters && <Button className={classes.addButton} onClick={() => setShowParameters(true)}><PlusIcon /> Add Parameters</Button>}
              {showParameters && <FaroParameterListInput
                classes={{ tableHead: classes.tableHead }}
                defaultParameters={parameters}
                hideKmsKeyArn
                onParametersChange={setParameters}
              />}
            </div>
          </div>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button onClick={onUpdate} color="primary" variant="contained">
          Update
        </Button>
      </DialogActions>
    </Dialog >
  );
};

export default TestActionDialog;
